import ConfirmationModal from '@components/ConfirmationModal'
import { ErrorNote } from '@components/Notice'
import { PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { useIdeas, useTrip, useTripDayInfos } from '@hooks'
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { dayFromParam } from '../core/selection'
import { useEventMutations } from '../useEventMutations'
import { EventCreateDraft, draftFromIdea, emptyDraft, hasChanges, toCreatePayload } from './core/draft'
import { EventCreateStep, bottomBar } from './core/presentation'
import EventFlow from './EventFlow'
import { EventCreateLocationState, eventCreateReturnPath, ideaIdFromParam } from './paths'

/**
 * The three-step create-event flow — What → When → Extras — as a full page on
 * the create-trip flow's chrome, like the iOS flow: back · "New Event" · close,
 * the labelled step indicator, the step's form and the bottom bar with the
 * event so far and its CTA. `?day=` preselects a day and `?idea=` fills the
 * flow from an idea; the event is only created by "Create Event", after which
 * — or on close — the flow returns to wherever it was opened from.
 */
const EventCreatePage: React.FC = () => {
	const { tripId = '' } = useParams<{ tripId: string }>()
	const [params] = useSearchParams()
	const navigate = useNavigate()
	const location = useLocation()
	const from = (location.state as EventCreateLocationState | null)?.from

	const { data: trip, isLoading, error } = useTrip(tripId)
	const { data: dayInfosData } = useTripDayInfos(tripId)
	const { data: ideasData, isLoading: ideasLoading } = useIdeas(tripId)
	const { addEvent } = useEventMutations(tripId)

	const dayParam = params.get('day')
	const ideaId = ideaIdFromParam(params.get('idea'))

	// What the flow opens on — the day it was opened for, filled from the idea
	// when there is one — and what closing measures changes against.
	const seed = useMemo<EventCreateDraft | null>(() => {
		if (!trip || (ideaId !== null && ideasLoading)) return null
		// `?day=` read as the itinerary reads it; the first day without one.
		const day = dayFromParam(dayParam, trip.dayCount) ?? 0
		const idea = ideaId === null ? undefined : ideasData?.ideas.find(candidate => candidate.id === ideaId)
		return idea ? draftFromIdea(idea, day) : emptyDraft(day)
	}, [trip, ideaId, ideasLoading, ideasData, dayParam])

	const [edited, setEdited] = useState<EventCreateDraft | null>(null)
	const [step, setStep] = useState<EventCreateStep>('what')
	const [isCreating, setIsCreating] = useState(false)
	const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false)

	if (error) return <ErrorNote>Error loading trip: {error.message}</ErrorNote>
	if (isLoading) return <PageLoader label="Loading trip…" />
	if (!trip) return <ErrorNote>Trip not found</ErrorNote>
	// The trip is in; an idea to fill the flow from may still be on its way.
	if (!seed) return <PageLoader label="Loading trip…" />

	const draft = edited ?? seed

	const leave = (dayIndex: number) => navigate(eventCreateReturnPath(tripId, from, dayIndex))

	const handleClose = () => {
		if (hasChanges(draft, seed)) setIsConfirmingDiscard(true)
		else leave(seed.tripDayIndex)
	}

	const handleCreate = async () => {
		setIsCreating(true)
		const created = await addEvent(toCreatePayload(draft), draft.files)
		setIsCreating(false)
		// A failed create keeps the draft; the mutation has said why.
		if (created) leave(draft.tripDayIndex)
	}

	return (
		<PageScroll>
			<EventFlow
				title="New Event"
				trip={trip}
				dayInfos={dayInfosData?.tripDayInfo}
				draft={draft}
				onChange={setEdited}
				step={step}
				onStepChange={setStep}
				bar={day => bottomBar(draft, step, day)}
				ctaPending={isCreating}
				onSubmit={handleCreate}
				onClose={handleClose}
			/>

			<ConfirmationModal
				isOpen={isConfirmingDiscard}
				onClose={() => setIsConfirmingDiscard(false)}
				onConfirm={() => leave(seed.tripDayIndex)}
				title="Discard this event?"
				message="What you've entered won't be saved."
				confirmText="Discard"
			/>
		</PageScroll>
	)
}

export default EventCreatePage
